import styled, { css } from 'styled-components'

import { FireCardProps } from './FireCard.types'

type DesktopImagePosition = NonNullable<
  NonNullable<FireCardProps['settings']>['imagePosition']
>['desktop']

type MobileImagePosition = NonNullable<
  NonNullable<FireCardProps['settings']>['imagePosition']
>['mobile']

const backgroundStyle = css`
  ${({ theme }) => css`
    background: ${theme.colours.primary.white};
    box-shadow: ${theme.shadows.small};
  `}
`

const noBackgroundStyle = css`
  border: 1px solid transparent;
  padding: 5px;

  &:hover,
  &:active,
  &:focus {
    ${({ theme }) => theme.helpers.borders};
  }
`

export const CardContainer = styled.div<{
  $desktopImagePosition: DesktopImagePosition
  $mobileImagePosition: MobileImagePosition
  $background: boolean
  $hasImage: boolean
}>`
  ${({
    theme,
    $mobileImagePosition,
    $desktopImagePosition,
    $background,
    $hasImage,
  }) => css`
    ${$background ? backgroundStyle : noBackgroundStyle}
    border-radius: 2px;
    display: flex;
    flex-direction: ${$mobileImagePosition === 'top' ? 'column' : 'row'};
    gap: ${$hasImage ? '10px' : 0};

    ${theme.media.up.md} {
      flex-direction: ${$desktopImagePosition === 'top' ? 'column' : 'row'};
      gap: 20px;
    }
  `}
`

const imageContainerLeftMobile = ({ $padding }: { $padding: boolean }) => css`
  padding: ${$padding ? '10px 0 10px 10px' : 0};
  width: 100px;
`

const imageContainerTopMobile = ({ $padding }: { $padding: boolean }) => css`
  padding: ${$padding ? '10px 10px 0' : 0};
  width: 100%;
`

const imageContainerLeftDesktop = ({ $padding }: { $padding: boolean }) => css`
  padding: ${$padding ? '10px 0 10px 10px' : 0};
  flex-basis: 25%;
  width: 100%;
`

const imageContainerTopDesktop = ({ $padding }: { $padding: boolean }) => css`
  padding: ${$padding ? '10px 10px 0' : 0};
  width: 100%;
`

export const FireCardImageContainer = styled.div<{
  $padding: boolean
  $desktopImagePosition: DesktopImagePosition
  $mobileImagePosition: MobileImagePosition
}>`
  ${({ theme, $padding, $mobileImagePosition, $desktopImagePosition }) => css`
    display: flex;
    flex-shrink: 0;
    position: relative;

    ${$mobileImagePosition === 'top'
      ? imageContainerTopMobile({ $padding })
      : imageContainerLeftMobile({ $padding })}
    ${theme.media.up.md} {
      flex-shrink: 1;

      ${$desktopImagePosition === 'top'
        ? imageContainerTopDesktop({ $padding })
        : imageContainerLeftDesktop({ $padding })}
    }
  `}
`

export const FireCardContentContainer = styled.div<{
  $desktopImagePosition: DesktopImagePosition
  $mobileImagePosition: MobileImagePosition
  $hasImage: boolean
}>`
  ${({ theme, $mobileImagePosition, $desktopImagePosition, $hasImage }) => css`
    padding: ${$hasImage
      ? $mobileImagePosition === 'top'
        ? '10px 20px 20px'
        : '20px 20px 20px 10px'
      : '20px 15px'};

    ${theme.media.up.md} {
      align-items: flex-start;
      display: flex;
      flex-basis: ${$desktopImagePosition === 'top' ? 'auto' : '75%'};
      flex-direction: column;
      justify-content: center;
      padding: ${$hasImage
        ? $desktopImagePosition === 'top'
          ? '10px 20px 20px'
          : '20px 20px 20px 10px'
        : '20px 15px'};
    }
  `}
`
